/**
 * _shared/upstreamError.ts — an upstream reply that names no cause is never an error message (errsay).
 *
 * 24 Oct 2026, TDS cockpit, "Build the return file" on TSL Infotech:
 *
 *     That step did not go through
 *     Unauthorized
 *
 * Karr's Sandbox sign-in had failed, sandbox-gst carried on with an empty token (PeriOne holds the GST
 * rail, so a failed Sandbox sign-in is not fatal there), and Sandbox's one-word answer to the empty token
 * went straight to the screen. "Unauthorized" says nothing: not who refused, not why, not what to do.
 *
 * The rule: a reply that is only a status word ("Unauthorized", "Forbidden", "Bad Request", "HTTP 401",
 * nothing at all) is replaced by what that status MEANS for this call and what to do about it. A reply
 * that does say something ("AUTH4033: Invalid Session", "Statement already furnished") is kept word for
 * word, because those words are the cause and other code matches on them.
 *
 * Plain TypeScript, no Deno or browser API: edge functions call `upstreamError` where they read an
 * upstream reply, and the app's reader (`edgeErrorMessage`) calls `bareReasonSentence` as the net under
 * every function that does not.
 */

/** A reply that is only an HTTP reason phrase, a status code, or empty. */
const BARE = /^\s*(?:https?\s*)?(?:status\s*)?(?:\d{3}\s*[:\-]?\s*)?(?:unauthori[sz]ed|forbidden|bad request|not found|internal server error|bad gateway|service unavailable|gateway time-?out|too many requests|unprocessable (?:entity|content)|conflict|method not allowed|request failed|error|failed|invalid request)?\s*[.!]?\s*$/i;

/** True when `message` names no cause: a bare reason phrase, a bare status code, or nothing. */
export function isBareReason(message: unknown): boolean {
    if (message === null || message === undefined) return true;
    if (typeof message !== 'string') return false;
    return BARE.test(message);
}

/** The status a bare reason phrase stands for, when the HTTP status itself was lost on the way. */
function statusOfWords(message: string): number | undefined {
    const code = message.match(/\b([1-5]\d\d)\b/);
    if (code) return Number(code[1]);
    const m = message.toLowerCase();
    if (/unauthori[sz]ed/.test(m)) return 401;
    if (/forbidden/.test(m)) return 403;
    if (/not found/.test(m)) return 404;
    if (/too many/.test(m)) return 429;
    if (/bad gateway/.test(m)) return 502;
    if (/unavailable/.test(m)) return 503;
    if (/time-?out/.test(m)) return 504;
    if (/internal server/.test(m)) return 500;
    if (/bad request|invalid request|unprocessable/.test(m)) return 400;
    return undefined;
}

const said = (message: unknown) => {
    const s = typeof message === 'string' ? message.trim().replace(/[.!]+$/, '') : '';
    return s ? `it answered only "${s}"` : 'it gave no reason';
};

/**
 * The sentence for a reply that named no cause. `service` is who answered ("Sandbox", "PeriOne");
 * left out, the sentence speaks of "the service behind this step". `fix` is what to do when the
 * cause is Karr's own key for that service (401/403), e.g. which secrets to renew.
 */
export function bareReasonSentence(
    opts: { service?: string; status?: number; message?: unknown; fix?: string },
): string {
    const svc = opts.service;
    const refused = svc ? `${svc} refused this step` : 'This step was refused';
    const them = svc ?? 'The service behind this step';
    const whose = svc ? `Karr's ${svc} key` : 'a key Karr holds for the service behind it';
    const word = typeof opts.message === 'string' ? opts.message : '';
    // A reason phrase outranks a relayed status: an edge function that relays "Unauthorized" answers 502.
    const status = statusOfWords(word) ?? opts.status;
    const nothing = 'Nothing was sent or changed.';
    const fix = (fallback: string) => ` ${opts.fix || fallback}`;
    switch (true) {
        case status === 401:
            return `${refused} (${said(word)}): ${whose} was not accepted. It has expired, been changed, or has no access to this API. ${nothing}${fix('Try once more; if it repeats, the Karr team must renew that key.')}`;
        case status === 403:
            return `${refused} (${said(word)}): ${whose} does not have this API switched on. ${nothing}${fix('The Karr team must enable it on that account.')}`;
        case status === 404:
            return `${them} did not find what this step asked for (${said(word)}). If it was only just created, wait a minute and try again.`;
        case status === 429:
            return `${them} is limiting how fast Karr can call it (${said(word)}). ${nothing} Try again in a minute.`;
        case status !== undefined && status >= 500:
            return `${them} had a fault of its own (HTTP ${status}; ${said(word)}). ${nothing} Try again in a few minutes.`;
        case status !== undefined:
            return `${refused} with HTTP ${status} (${said(word)}). ${nothing}`;
        default:
            return `${refused} (${said(word)}). ${nothing}`;
    }
}

/**
 * The error text for an upstream reply: its own words when they name a cause, else what the status
 * means. Every edge call that reads a third party's error body goes through this.
 */
export function upstreamError(
    opts: { service: string; status?: number; message?: unknown; fix?: string },
): string {
    const m = opts.message;
    if (typeof m === 'string' && !isBareReason(m)) return m;
    if (m !== null && m !== undefined && typeof m !== 'string') return JSON.stringify(m);
    return bareReasonSentence(opts);
}

/** What to do when Karr's own Sandbox key is refused (sandboxCall, sandbox-gst). */
export const SANDBOX_KEY_FIX =
    'To fix it, the Karr team updates SANDBOX_API_KEY and SANDBOX_API_SECRET in the Supabase project secrets from the Sandbox console (API keys), then tries again.';
